import { X } from "lucide-react";
import { ReactNode } from "react";

import { useIsKeyboardOpen } from "../../hooks/useIsKeyboardOpen";
import { twMerge } from "../tailwind-utils";

import * as Base from "./base-modal";

interface IHeaderProps {
  title: string;

  hideCloseButton?: boolean;
}

const CloseElement = ({ className }: { className?: string }) => (
  <Base.ResponsiveDialogClose
    className={twMerge(
      "tw-absolute tw-right-0 tw-top-11 tw-cursor-pointer tw-pr-6 tw-text-2xl tw-font-bold tw-text-gray-400 hover:tw-text-gray-600 focus:tw-outline-none focus-visible:tw-outline-none active:tw-outline-none",
      className,
    )}
  >
    <X className="tw-h-4 tw-w-4" strokeWidth={4} />
    <span className="tw-sr-only">Close</span>
  </Base.ResponsiveDialogClose>
);

const Header = ({ title, hideCloseButton }: IHeaderProps) => {
  return (
    <div
      className="tw-flex tw-w-full tw-justify-center tw-rounded-t-xl tw-border-0 tw-border-b tw-border-solid tw-border-gray-200 tw-py-[5px]"
      id="dialog-header"
    >
      <h5 className="tw-mt-2">{title}</h5>
      {!hideCloseButton && <CloseElement />}
    </div>
  );
};

interface IResponsiveDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  children: ReactNode;

  onClose?: () => void;
  title?: string;
  lockOutsideInteraction?: boolean;
  className?: string;
  footer?: ReactNode;
  hideCloseButton?: boolean;
}

export const AppModal = ({
  open,
  setOpen,
  children,

  onClose,
  title,
  lockOutsideInteraction = true,
  className,
  footer,
  hideCloseButton = false,
}: IResponsiveDialogProps) => {
  const isKeyboardOpen = useIsKeyboardOpen(open);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setOpen(nextOpen);
    } else {
      onClose?.();
      setOpen(nextOpen);
    }
  };

  return (
    <Base.ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <Base.ResponsiveDialogContent
        className="tw-overflow-y-visible [&_button#-default-dialog-close-icon]:tw-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (lockOutsideInteraction) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (lockOutsideInteraction) e.preventDefault();
        }}
        id="dialog-content"
      >
        {title && (
          <Base.ResponsiveDialogTitle>
            <Header title={title} hideCloseButton={hideCloseButton} />
          </Base.ResponsiveDialogTitle>
        )}
        <div
          className={twMerge(
            "tw-w-full tw-overflow-y-auto tw-p-4",
            "[&::-webkit-scrollbar]:tw-w-1 [&::-webkit-scrollbar-track]:tw-bg-gray-100 [&::-webkit-scrollbar-track]:tw-rounded-full [&::-webkit-scrollbar-thumb]:tw-bg-gray-300 [&::-webkit-scrollbar-thumb]:tw-rounded-3xl ",
            "tw-max-h-full",
            { "tw-max-h-[200px] tw-pb-12": isKeyboardOpen },

            className,
          )}
        >
          {children}
        </div>
        {footer && (
          <Base.ResponsiveDialogFooter className="tw-mb-3">
            {footer}
          </Base.ResponsiveDialogFooter>
        )}
      </Base.ResponsiveDialogContent>
    </Base.ResponsiveDialog>
  );
};
